import { useState } from 'react';
import { useApi } from '../api.js';
import { ClickableValue } from './DrillDown.jsx';

function fmt$(n) {
  if (n == null) return '—';
  return '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fyForDate(d) { return d.getMonth() >= 5 ? d.getFullYear() + 1 : d.getFullYear(); }

export default function Billing() {
  const [period, setPeriod] = useState('month');
  const { data, error, loading, refresh } = useApi('/api/billing?period=' + period);

  if (loading) return <div className="loading">Loading billing…</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const { period: range = {}, totalAmount = 0, totalLoads = 0, customers = 0, loadsInWindow } = data || {};
  // Defensive: older cached responses (localStorage seed in useApi) stored
  // byCustomer as a Map serialization ({}), which crashes .map. Coerce to
  // an array so stale cache never renders as a runtime error.
  const byCustomer = Array.isArray(data && data.byCustomer) ? data.byCustomer : [];
  const fy = fyForDate(new Date());

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Revenue Recognition</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select className="refresh-btn" value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="week">This week (7 days)</option>
            <option value="last30">Last 30 days</option>
            <option value="month">Month-to-date</option>
          </select>
          <button className="refresh-btn" onClick={refresh}>Refresh</button>
        </div>
      </div>

      <div className="note" style={{ marginBottom: '0.8rem' }}>
        Period: <strong>{range.start}</strong> → <strong>{range.end}</strong>
        {' '}· recognized revenue from all loads received (approved + pending review) × tier rate per customer.
        {loadsInWindow && loadsInWindow.pending > 0 && (
          <span style={{ marginLeft: 6, background: '#fef9c3', color: '#854d0e', padding: '1px 8px', borderRadius: 3, fontWeight: 500 }}>
            includes {loadsInWindow.pending} pending review load{loadsInWindow.pending === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div className="cards">
        <div className="card">
          <div className="label">Recognized revenue</div>
          <div className="value">{fmt$(totalAmount)}</div>
        </div>
        <div className="card">
          <div className="label">Total loads</div>
          <div className="value">
            <ClickableValue query={{ start: range.start, end: range.end }} title={`Loads ${range.start} → ${range.end}`}>{totalLoads}</ClickableValue>
          </div>
        </div>
        <div className="card">
          <div className="label">Active customers</div>
          <div className="value">{customers}</div>
        </div>
        <div className="card">
          <div className="label">Avg $ / load</div>
          <div className="value">{totalLoads > 0 ? fmt$(totalAmount / totalLoads) : '—'}</div>
        </div>
      </div>

      <div className="chart-card">
        <h3>By customer — {range.start} → {range.end}</h3>
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th className="num">Small</th>
              <th className="num">Regular</th>
              <th className="num">18m³</th>
              <th className="num">28m³</th>
              <th className="num">Total loads</th>
              <th className="num">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {byCustomer.map((c) => (
              <tr key={c.customer}>
                <td>{c.customer}</td>
                <td className="num">
                  {c.smallLoads > 0
                    ? <ClickableValue query={{ start: range.start, end: range.end, customer: c.customer, tier: 'small' }} title={`${c.customer} — Small loads`}>{c.smallLoads}</ClickableValue>
                    : 0} {c.smallAmount > 0 && <span className="small">({fmt$(c.smallAmount)})</span>}
                </td>
                <td className="num">
                  {c.regularLoads > 0
                    ? <ClickableValue query={{ start: range.start, end: range.end, customer: c.customer, tier: 'regular' }} title={`${c.customer} — Regular loads`}>{c.regularLoads}</ClickableValue>
                    : 0} {c.regularAmount > 0 && <span className="small">({fmt$(c.regularAmount)})</span>}
                </td>
                <td className="num">
                  {c.oversize18Loads > 0
                    ? <ClickableValue query={{ start: range.start, end: range.end, customer: c.customer, tier: 'oversize18' }} title={`${c.customer} — Oversize 18m³ loads`}>{c.oversize18Loads}</ClickableValue>
                    : 0} {c.oversize18Amount > 0 && <span className="small">({fmt$(c.oversize18Amount)})</span>}
                </td>
                <td className="num">
                  {c.oversize28Loads > 0
                    ? <ClickableValue query={{ start: range.start, end: range.end, customer: c.customer, tier: 'oversize28' }} title={`${c.customer} — Oversize 28m³ loads`}>{c.oversize28Loads}</ClickableValue>
                    : 0} {c.oversize28Amount > 0 && <span className="small">({fmt$(c.oversize28Amount)})</span>}
                </td>
                <td className="num">
                  <ClickableValue query={{ start: range.start, end: range.end, customer: c.customer }} title={`${c.customer} — all loads in period`}>{c.totalLoads}</ClickableValue>
                </td>
                <td className="num"><strong>{fmt$(c.totalAmount)}</strong></td>
              </tr>
            ))}
            {byCustomer.length === 0 && (
              <tr><td colSpan="7" className="note">No loads in this period.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
